import { useCallback, useEffect, useState } from "react";
import { takeAfterLogin, useAuth } from "./lib/auth";
import { useUI } from "./lib/store";
import { JoinScreen, LoginScreen, PrintView, TripsScreen } from "./components/screens";
import { TripView } from "./components/TripView";
import { Spinner, Toasts } from "./components/ui";

function usePath() {
  const [path, setPath] = useState(location.pathname);
  useEffect(() => {
    const h = () => setPath(location.pathname);
    window.addEventListener("popstate", h);
    return () => window.removeEventListener("popstate", h);
  }, []);
  const nav = useCallback((p: string, replace = false) => {
    if (replace) history.replaceState(null, "", p);
    else history.pushState(null, "", p);
    setPath(p);
  }, []);
  return [path, nav] as const;
}

function useTheme() {
  const theme = useUI((s) => s.theme);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      const dark = theme === "dark" || (theme === "system" && mq.matches);
      document.documentElement.dataset.theme = dark ? "dark" : "light";
    };
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [theme]);
}

const LAST_TRIP = "tb:last-trip";

export function App() {
  const { user, loading } = useAuth();
  const [path, nav] = usePath();
  useTheme();

  // после входа через Google — вернуться туда, откуда уходили
  useEffect(() => {
    if (!loading && user && path === "/auth/callback") nav(takeAfterLogin() ?? "/", true);
  }, [loading, user, path, nav]);

  const join = path.match(/^\/join\/([A-Za-z0-9_-]+)/);
  const print = path.match(/^\/t\/([0-9a-f-]{36})\/print/);
  const trip = path.match(/^\/t\/([0-9a-f-]{36})/);

  // с главной — сразу в последнюю открытую поездку
  useEffect(() => {
    if (!user || path !== "/") return;
    try {
      const last = localStorage.getItem(LAST_TRIP);
      if (last && !sessionStorage.getItem("tb:list")) nav(`/t/${last}`, true);
    } catch {
      /* ignore */
    }
  }, [user, path, nav]);

  if (loading || path === "/auth/callback")
    return (
      <div className="grid h-dvh place-items-center">
        <Spinner size={28} className="text-[var(--accent)]" />
      </div>
    );

  let content: React.ReactNode;
  if (join) content = <JoinScreen token={join[1]} onJoined={(id) => nav(`/t/${id}`, true)} />;
  else if (!user) content = <LoginScreen />;
  else if (print) content = <PrintView tripId={print[1]} />;
  else if (trip) {
    try {
      localStorage.setItem(LAST_TRIP, trip[1]);
    } catch {
      /* ignore */
    }
    content = (
      <TripView
        key={trip[1]}
        tripId={trip[1]}
        onBack={() => {
          try {
            sessionStorage.setItem("tb:list", "1");
          } catch {
            /* ignore */
          }
          nav("/");
        }}
      />
    );
  } else content = <TripsScreen onOpen={(id) => nav(`/t/${id}`)} />;

  return (
    <>
      {content}
      <Toasts />
    </>
  );
}
